
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MessageSquare, Minus, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

interface PlanData {
  name: string;
  basePrice: number;
  whatsappIncluded: number;
  whatsappPrice: number;
  negotiationsIncluded: number;
  extraNegotiationsPrice: number;
}

import { planData } from '@/constants/planData';

export default function ConfigurarPlano() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan || 'Advanced';
  const customerId = location.state?.customerId || sessionStorage.getItem('asaasCustomerId');
  const firebaseCustomerId = location.state?.firebaseCustomerId || sessionStorage.getItem('firebaseCustomerId');
  const customerDataFromState = location.state?.customerData;
  const [plan, setPlan] = useState<PlanData>(planData[selectedPlan]);
  
  // Retrieve customer data from sessionStorage if not in location state
  const [customerData] = useState(() => {
    if (customerDataFromState) return customerDataFromState;
    const storedData = sessionStorage.getItem('customerData');
    return storedData ? JSON.parse(storedData) : null;
  });

const [whatsappAccounts, setWhatsappAccounts] = useState(plan.whatsappIncluded);
const [extraNegotiations, setExtraNegotiations] = useState(0);

// Sempre obter os valores do plano selecionado diretamente do planData
const currentPlanData = planData[selectedPlan];
const basePrice = currentPlanData.basePrice;
const extraWhatsappCost = Math.max(0, whatsappAccounts - currentPlanData.whatsappIncluded) * currentPlanData.whatsappPrice;
const extraNegotiationsCost = extraNegotiations * currentPlanData.extraNegotiationsPrice;
const totalPrice = basePrice + extraWhatsappCost + extraNegotiationsCost;
  
  useEffect(() => {
    // Verificar se temos um plano selecionado
    if (!location.state?.selectedPlan) {
      navigate('/', { replace: true });
      return;
    }
    
    // Verificar se temos dados do cliente
    if (!customerId && !sessionStorage.getItem('asaasCustomerId')) {
      toast.error('Dados do cliente não encontrados. Redirecionando para cadastro.');
      navigate('/registro-cliente', { state: { selectedPlan } });
      return;
    }
    
    // Atualizar o plano quando mudar a seleção
    setPlan(planData[selectedPlan]);
    setWhatsappAccounts(planData[selectedPlan].whatsappIncluded);
    setExtraNegotiations(0);
  }, [selectedPlan, location.state, customerId, navigate]);
  
  const incrementWhatsapp = () => {
    // No plano Basic não pode adicionar contas extras
    if (selectedPlan === 'Basic') {
      toast.error('O plano Basic não permite contas WhatsApp adicionais');
      return;
    }
    setWhatsappAccounts(prev => prev + 1);
  };
  
  const decrementWhatsapp = () => {
    if (whatsappAccounts > plan.whatsappIncluded) {
      setWhatsappAccounts(prev => prev - 1);
    } else {
      toast.error(`O plano ${plan.name} inclui no mínimo ${plan.whatsappIncluded} contas WhatsApp`);
    }
  };
  
  const incrementNegotiations = () => {
    // No Enterprise, as negociações são customizáveis
    if (selectedPlan === 'Enterprise') {
      toast.info('Para o plano Enterprise, entre em contato para discutir volumes personalizados');
      return;
    }
    setExtraNegotiations(prev => prev + 1);
  };
  
  const decrementNegotiations = () => {
    if (extraNegotiations > 0) {
      setExtraNegotiations(prev => prev - 1);
    }
  };
  
  const handleProceedToCheckout = () => {
    const checkoutData = {
      plan: selectedPlan,
      planName: plan.name,
      whatsappAccounts,
      extraWhatsappAccounts: Math.max(0, whatsappAccounts - plan.whatsappIncluded),
      negotiationsIncluded: plan.negotiationsIncluded,
      extraNegotiationsPacks: extraNegotiations,
      basePrice,
      extraWhatsappCost,
      extraNegotiationsCost,
      totalPrice,
      customerId,
      firebaseCustomerId,
      customerData
    };
    
    toast.success('Redirecionando para o pagamento...');
    console.log('Checkout data:', checkoutData);
    
    // Armazenar dados do checkout para uso na página de pagamento
    sessionStorage.setItem('checkoutData', JSON.stringify(checkoutData));
    
    // Redirecionar para a página de pagamento com os dados do checkout
    navigate('/pagamento', { state: checkoutData });
  };
  
  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="py-4 bg-white border-b border-border">
        <div className="container px-4 mx-auto">
          <div className="flex items-center">
            <img src="/brand/logo-secundario.svg" alt="dgenny" className="h-9" />
          </div>
        </div>
      </header>

      <main className="container px-4 mx-auto py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center text-brand-azul/70 hover:text-brand-roxo transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} className="mr-1" /> Voltar para planos
            </button>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-brand-azul mb-6">Configure o plano {selectedPlan}</h1>

          {/* Breadcrumbs */}
          <div className="flex items-center text-sm text-brand-azul/60 mb-8">
            <span>Seleção de plano</span>
            <ChevronRight size={16} className="mx-2" />
            <span className="font-medium text-brand-azul">Configuração</span>
            <ChevronRight size={16} className="mx-2" />
            <span>Pagamento</span>
          </div>

          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold text-brand-azul mb-4">Plano {selectedPlan} - Configurações</h2>
            
            {/* WhatsApp Accounts */}
            <div className="mb-6 p-4 border rounded-lg bg-muted/50">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-medium flex items-center">
                    <MessageSquare size={18} className="mr-2 text-brand-roxo" /> 
                    Contas WhatsApp
                  </h3>
                  <p className="text-sm text-brand-azul/60">
                    O plano {selectedPlan} inclui {plan.whatsappIncluded} contas
                    {plan.whatsappPrice > 0 ? ` (+R$${plan.whatsappPrice}/conta adicional)` : ''}
                  </p>
                </div>
                <div className="flex items-center">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={decrementWhatsapp}
                    disabled={whatsappAccounts <= plan.whatsappIncluded}
                  >
                    <Minus size={16} />
                  </Button>
                  <span className="mx-4 font-medium text-lg w-8 text-center">{whatsappAccounts}</span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={incrementWhatsapp}
                    disabled={selectedPlan === 'Basic'}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
              
              {selectedPlan === 'Basic' && (
                <div className="text-xs bg-amber-50 text-amber-800 p-2 rounded">
                  O plano Basic inclui 1 conta WhatsApp sem possibilidade de adição. 
                  Para adicionar mais contas, considere o plano Advanced.
                </div>
              )}
            </div>
            
            {/* Extra Negotiations */}
            <div className="mb-6 p-4 border rounded-lg bg-muted/50">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-medium">Pacotes de Negociações Adicionais</h3>
                  <p className="text-sm text-brand-azul/60">
                    O plano {selectedPlan} inclui {plan.negotiationsIncluded} negociações/mês
                    {plan.extraNegotiationsPrice > 0 ? 
                      ` (+R$${plan.extraNegotiationsPrice}/100 negociações adicionais)` : 
                      selectedPlan === 'Enterprise' ? ' (volumes customizáveis)' : ''}
                  </p>
                </div>
                <div className="flex items-center">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={decrementNegotiations}
                    disabled={extraNegotiations <= 0}
                  >
                    <Minus size={16} />
                  </Button>
                  <span className="mx-4 font-medium text-lg w-8 text-center">{extraNegotiations}</span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={incrementNegotiations}
                    disabled={selectedPlan === 'Enterprise'}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
              
              {selectedPlan === 'Enterprise' && (
                <div className="text-xs bg-amber-50 text-amber-800 p-2 rounded">
                  Para o plano Enterprise, os volumes são personalizados conforme necessidade.
                  Nossa equipe entrará em contato para definir os detalhes.
                </div>
              )}
              
              {extraNegotiations > 0 && (
                <div className="text-xs text-brand-azul/60">
                  + {extraNegotiations} {extraNegotiations === 1 ? 'pacote' : 'pacotes'} de 100 negociações adicionais 
                  = {' '}
                  <span className="font-medium">
                    {plan.negotiationsIncluded + (extraNegotiations * 100)} negociações/mês
                  </span>
                </div>
              )}
            </div>
            
            {/* Summary */}
            <div className="mb-6 p-4 border-t pt-6">
              <h3 className="font-medium mb-4">Resumo do Plano</h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span>Plano {selectedPlan} (mensal)</span>
                  <span>R${basePrice.toFixed(2)}</span>
                </div>
                
                {extraWhatsappCost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>
                      {whatsappAccounts - plan.whatsappIncluded} contas WhatsApp adicionais
                    </span>
                    <span>R${extraWhatsappCost.toFixed(2)}</span>
                  </div>
                )}
                
                {extraNegotiationsCost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>
                      {extraNegotiations} {extraNegotiations === 1 ? 'pacote' : 'pacotes'} de negociações adicionais
                    </span>
                    <span>R${extraNegotiationsCost.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between font-bold text-brand-azul border-t pt-4">
                <span>Total mensal</span>
                <span>R${totalPrice.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                className="bg-brand-roxo hover:bg-brand-roxo/90 text-white py-2 px-6 rounded-lg font-medium flex items-center justify-center gap-2"
                onClick={handleProceedToCheckout}
              >
                Prosseguir para pagamento <ArrowRight size={16} />
              </Button>
            </div>
          </Card>
          
          <div className="text-center text-sm text-brand-azul/60">
            <p>
              Ao prosseguir, você concorda com nossos <a href="#" className="underline hover:text-brand-azul">Termos de Serviço</a> e 
              {' '}<a href="/politica-de-privacidade" className="underline hover:text-brand-azul">Política de Privacidade</a>.
            </p>
            <p className="mt-2">
              Precisa de ajuda? <a href="#" className="text-brand-azul hover:underline">Fale conosco</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
